import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
  Snackbar
} from "@material-ui/core";
import { Delete as DeleteIcon, FileCopy as CopyIcon } from "@material-ui/icons";
import {
  useMyApiTokensQuery,
  useCreateApiTokenMutation,
  useRevokeApiTokenMutation
} from "@graphql";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function ApiTokensDialog({ open, onClose }: Props) {
  const [name, setName] = useState("");
  const [createdToken, setCreatedToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, loading, refetch } = useMyApiTokensQuery({
    fetchPolicy: "network-only",
    skip: !open
  });

  const [createToken, { loading: creating }] = useCreateApiTokenMutation();
  const [revokeToken] = useRevokeApiTokenMutation();

  const handleCreate = async () => {
    if (!name.trim()) return;

    const result = await createToken({ variables: { name: name.trim() } });
    if (result.data) {
      setCreatedToken(result.data.createApiToken.token);
      setName("");
      refetch();
    }
  };

  const handleRevoke = async (id: string) => {
    await revokeToken({ variables: { id } });
    refetch();
  };

  const handleCopy = () => {
    if (createdToken) {
      navigator.clipboard.writeText(createdToken);
      setCopied(true);
    }
  };

  const tokens = data?.myApiTokens ?? [];
  const activeTokens = tokens.filter((t) => !t.revoked);
  const revokedTokens = tokens.filter((t) => t.revoked);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>API Tokens</DialogTitle>
      <DialogContent>
        {createdToken && (
          <div
            style={{
              background: "#e8f5e9",
              padding: 16,
              borderRadius: 4,
              marginBottom: 16
            }}
          >
            <Typography variant="body2" gutterBottom>
              <strong>Token created!</strong> Copy it now — you won't see it
              again.
            </Typography>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <code
                style={{
                  flex: 1,
                  wordBreak: "break-all",
                  fontSize: 12,
                  background: "#fff",
                  padding: 8,
                  borderRadius: 4
                }}
              >
                {createdToken}
              </code>
              <IconButton size="small" onClick={handleCopy}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <TextField
            label="Token name"
            placeholder="e.g. My CLI token"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            size="small"
            variant="outlined"
            style={{ flex: 1 }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreate}
            disabled={creating || !name.trim()}
          >
            Create
          </Button>
        </div>

        {loading && <Typography color="textSecondary">Loading...</Typography>}

        {activeTokens.length > 0 && (
          <>
            <Typography variant="subtitle2" gutterBottom>
              Active tokens
            </Typography>
            <List dense>
              {activeTokens.map((token) => (
                <ListItem key={token.id}>
                  <ListItemText
                    primary={token.name}
                    secondary={
                      <>
                        Created {new Date(token.createdOn).toLocaleDateString()}
                        {token.lastUsedOn &&
                          ` · Last used ${new Date(token.lastUsedOn).toLocaleDateString()}`}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRevoke(token.id)}
                      title="Revoke token"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </>
        )}

        {revokedTokens.length > 0 && (
          <>
            <Typography
              variant="subtitle2"
              gutterBottom
              style={{ marginTop: 8 }}
            >
              Revoked tokens
            </Typography>
            <List dense>
              {revokedTokens.map((token) => (
                <ListItem key={token.id}>
                  <ListItemText
                    primary={
                      <span style={{ textDecoration: "line-through" }}>
                        {token.name}
                      </span>
                    }
                    secondary={`Created ${new Date(token.createdOn).toLocaleDateString()}`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {!loading && tokens.length === 0 && (
          <Typography color="textSecondary" variant="body2">
            No API tokens yet. Create one to authenticate MCP clients.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        message="Token copied to clipboard"
      />
    </Dialog>
  );
}
